import { useCallback, useEffect, useState } from 'react';
import {
  createTraining,
  fetchTraining,
  fetchTrainings,
  recordTrainingEffectiveness,
  updateTraining,
} from './competenceApi';
import { labelTrainingStatus, labelTrainingType, trainingStatusBadgeClass } from './competenceUtils';
import type { TrainingSession, TrainingStatus, TrainingType } from './types';

interface TrainingsTabProps {
  canEdit: boolean;
}

export function TrainingsTab({ canEdit }: TrainingsTabProps) {
  const [sessions, setSessions] = useState<TrainingSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<TrainingSession | null>(null);
  const [effectiveness, setEffectiveness] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [topic, setTopic] = useState('');
  const [type, setType] = useState<TrainingType>('interna');
  const [scheduledDate, setScheduledDate] = useState('');
  const [status, setStatus] = useState<TrainingStatus>('planificada');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchTrainings();
      setSessions(data);
    } catch {
      setError('No se pudieron cargar las capacitaciones.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      return;
    }
    void fetchTraining(selectedId)
      .then(setDetail)
      .catch(() => setError('No se pudo cargar el detalle de la capacitación.'));
  }, [selectedId]);

  const openCreate = () => {
    setFormOpen(true);
    setTitle('');
    setTopic('');
    setType('interna');
    setScheduledDate('');
    setStatus('planificada');
  };

  const handleCreate = async () => {
    if (!title.trim() || !scheduledDate) return;
    setSaving(true);
    try {
      await createTraining({
        title: title.trim(),
        topic: topic.trim() || null,
        type,
        scheduledDate: new Date(scheduledDate).toISOString(),
        status,
      });
      setFormOpen(false);
      await load();
    } catch {
      setError('No se pudo crear la capacitación.');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (session: TrainingSession, nextStatus: TrainingStatus) => {
    await updateTraining(session.id, { status: nextStatus });
    await load();
    if (selectedId === session.id) {
      const refreshed = await fetchTraining(session.id);
      setDetail(refreshed);
    }
  };

  const handleEffectiveness = async () => {
    if (!selectedId || !effectiveness.trim()) return;
    await recordTrainingEffectiveness(selectedId, effectiveness.trim());
    setEffectiveness('');
    const refreshed = await fetchTraining(selectedId);
    setDetail(refreshed);
    await load();
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        {canEdit && (
          <button
            type="button"
            className="text-sm rounded bg-primary text-primary-foreground px-3 py-1.5"
            onClick={openCreate}
          >
            Nueva capacitación
          </button>
        )}
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}
      {loading ? (
        <p className="text-sm text-muted-foreground">Cargando…</p>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          <div className="overflow-x-auto border rounded-md">
            <table className="min-w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left p-2">Título</th>
                  <th className="text-left p-2">Fecha</th>
                  <th className="text-left p-2">Estado</th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((session) => (
                  <tr
                    key={session.id}
                    className={`border-t border-border cursor-pointer hover:bg-muted/30 ${
                      selectedId === session.id ? 'bg-muted/40' : ''
                    }`}
                    onClick={() => setSelectedId(session.id)}
                  >
                    <td className="p-2 font-medium">{session.title}</td>
                    <td className="p-2">
                      {new Date(session.scheduledDate).toLocaleDateString()}
                    </td>
                    <td className="p-2">
                      <span
                        className={`inline-block rounded px-2 py-0.5 text-xs ${trainingStatusBadgeClass(session.status)}`}
                      >
                        {labelTrainingStatus(session.status)}
                      </span>
                    </td>
                  </tr>
                ))}
                {sessions.length === 0 && (
                  <tr>
                    <td colSpan={3} className="p-4 text-center text-muted-foreground">
                      Sin sesiones de capacitación.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="border rounded-md p-4 min-h-[12rem]">
            {!detail ? (
              <p className="text-sm text-muted-foreground">
                Seleccione una capacitación para ver el detalle.
              </p>
            ) : (
              <div className="space-y-3 text-sm">
                <h3 className="font-medium text-base">{detail.title}</h3>
                <p className="text-muted-foreground">
                  {labelTrainingType(detail.type)} ·{' '}
                  {new Date(detail.scheduledDate).toLocaleString()}
                </p>
                {detail.topic && <p>Tema: {detail.topic}</p>}
                {detail.provider && <p>Proveedor: {detail.provider}</p>}
                <p>
                  Asistentes: {detail.attendees?.length ?? detail._count?.attendees ?? 0}
                </p>
                {detail.effectivenessEvaluated && (
                  <p className="text-green-700 dark:text-green-400">
                    Eficacia evaluada: {detail.effectivenessResult}
                  </p>
                )}
                {canEdit && (
                  <div className="flex flex-wrap gap-2 pt-2">
                    {detail.status === 'planificada' && (
                      <button
                        type="button"
                        className="text-xs rounded border px-2 py-1"
                        onClick={() => void handleStatusChange(detail, 'en_curso')}
                      >
                        Iniciar
                      </button>
                    )}
                    {detail.status === 'en_curso' && (
                      <button
                        type="button"
                        className="text-xs rounded border px-2 py-1"
                        onClick={() => void handleStatusChange(detail, 'completada')}
                      >
                        Completar
                      </button>
                    )}
                  </div>
                )}
                {canEdit && detail.status === 'completada' && !detail.effectivenessEvaluated && (
                  <div className="space-y-2 border-t border-border pt-3">
                    <p className="text-xs font-medium">Evaluación de eficacia</p>
                    <textarea
                      className="w-full border rounded px-2 py-1 text-sm bg-background"
                      rows={3}
                      placeholder="Resultado de la evaluación de eficacia"
                      value={effectiveness}
                      onChange={(e) => setEffectiveness(e.target.value)}
                    />
                    <button
                      type="button"
                      disabled={!effectiveness.trim()}
                      className="text-xs rounded bg-primary text-primary-foreground px-2 py-1 disabled:opacity-50"
                      onClick={() => void handleEffectiveness()}
                    >
                      Registrar eficacia
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {canEdit && formOpen && (
        <div className="border rounded-md p-4 space-y-3 bg-muted/20">
          <h3 className="font-medium">Nueva capacitación</h3>
          <input
            className="w-full border rounded px-2 py-1.5 text-sm bg-background"
            placeholder="Título"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          <input
            className="w-full border rounded px-2 py-1.5 text-sm bg-background"
            placeholder="Tema (opcional)"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
          />
          <div className="flex gap-2">
            <select
              className="border rounded px-2 py-1.5 text-sm bg-background"
              value={type}
              onChange={(e) => setType(e.target.value as TrainingType)}
            >
              <option value="interna">Interna</option>
              <option value="externa">Externa</option>
            </select>
            <input
              type="datetime-local"
              className="flex-1 border rounded px-2 py-1.5 text-sm bg-background"
              value={scheduledDate}
              onChange={(e) => setScheduledDate(e.target.value)}
            />
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={saving}
              className="text-sm rounded bg-primary text-primary-foreground px-3 py-1.5 disabled:opacity-50"
              onClick={() => void handleCreate()}
            >
              Crear
            </button>
            <button
              type="button"
              className="text-sm rounded border px-3 py-1.5"
              onClick={() => setFormOpen(false)}
            >
              Cancelar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
